import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { get } from 'scriptjs';
import { OrdersWithPayment } from 'src/app/models/order';
import { Store } from 'src/app/models/store';
import { OrderService } from 'src/app/order.service';
import { PaymentGatewayService } from 'src/app/_services/payment-gateway.service';
import { AnalyticsService } from 'src/app/_services/analytics.service';

declare var ApplePaySession: any;
declare var google: any;

const GOOGLE_PAY_JS = 'https://pay.google.com/gp/p/js/pay.js';
const CARD_NETWORKS = ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'];

/**
 * ENG-5728: Apple Pay / Google Pay on the order summary page.
 *
 * One tap pays the order here. The wallet hands the browser an encrypted token, the backend runs
 * the sale through Payroc and closes the order. Nothing redirects to the gateway's hosted page.
 * Apple Pay shows only where Safari can make payments; Google Pay only where Google says the
 * device is ready. On everything else the component renders nothing.
 */
@Component({
  selector: 'app-wallet-pay-buttons',
  templateUrl: './wallet-pay-buttons.component.html',
  styleUrls: ['./wallet-pay-buttons.component.scss']
})
export class WalletPayButtonsComponent implements OnInit {
  @Input() orderWithPayment: OrdersWithPayment;
  @Input() store: Store;
  @Input() orderHash: string;
  @Input() orderName: string;
  @Input() orderInstruction: string;
  @Input() disabled = false;

  @ViewChild('googleButton') googleButton: ElementRef<HTMLDivElement>;

  config: any = null;
  showApple = false;
  showGoogle = false;
  /** Which wallet the guest is paying with, so only the other button dims. */
  activeWallet: 'APPLEPAY' | 'GOOGLEPAY' | null = null;

  private paymentsClient: any;

  constructor(
    private paymentGatewayService: PaymentGatewayService,
    private orderService: OrderService,
    private router: Router,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    if (!this.eligible()) { return; }
    this.paymentGatewayService.getWalletConfig(+this.store.id, this.orderHash).subscribe(
      (config: any) => {
        if (!config || !config.enabled) { return; }
        this.config = config;
        this.setUpApple();
        this.setUpGoogle();
      },
      () => { /* no buttons; the wallet dialog route still works */ }
    );
  }

  private eligible(): boolean {
    return !!this.store && this.store.payment_gateway === 'WORLDNET'
      && !!this.store.has_card_payment && this.store.isEnabled('MOBILE_PAYMENT');
  }

  get visible(): boolean {
    return this.showApple || this.showGoogle;
  }

  get paying(): boolean {
    return this.activeWallet !== null;
  }

  /** A button dims only while the guest is paying with the other one. */
  dimmed(wallet: 'APPLEPAY' | 'GOOGLEPAY'): boolean {
    return this.disabled || (this.paying && this.activeWallet !== wallet);
  }

  private get total(): number {
    return Number(this.orderWithPayment?.payment?.total || 0);
  }

  // ---------------------------------------------------------------- Apple Pay

  private setUpApple() {
    try {
      const w = window as any;
      this.showApple = !!w.ApplePaySession && ApplePaySession.canMakePayments();
    } catch (e) {
      this.showApple = false;
    }
    this.cdr.markForCheck();
  }

  payWithApple() {
    if (!this.preflight()) { return; }
    this.activeWallet = 'APPLEPAY';
    const amount = this.total.toFixed(2);
    const session = new ApplePaySession(3, {
      countryCode: 'US',
      currencyCode: 'USD',
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      merchantCapabilities: ['supports3DS'],
      // Payroc's own example payload carries billingContact, and the card networks want an
      // address for a card-not-present sale.
      requiredBillingContactFields: ['postalAddress'],
      total: { label: this.config.store_name || 'Order', amount: amount }
    });

    session.onvalidatemerchant = (event: any) => {
      this.paymentGatewayService
        .startApplePaySession(+this.store.id, this.orderHash, event.validationURL)
        .subscribe(
          merchantSession => {
            // Apple throws here when the session does not match this domain or is malformed, and
            // the sheet then dies with a bare "Payment Not Completed" that says nothing.
            try {
              session.completeMerchantValidation(merchantSession);
            } catch (e) {
              console.error('[ApplePay] merchant validation rejected', e);
              this.abort(session, 'Apple Pay could not verify this site. Please use another payment method.');
            }
          },
          err => {
            console.error('[ApplePay] merchant session failed', err);
            this.abort(session, this.messageOf(err, 'Apple Pay could not be started. Please use another payment method.'));
          }
        );
    };

    session.onerror = (event: any) => {
      console.error('[ApplePay] session error', event);
      this.fail('Apple Pay could not be completed. Please use another payment method.');
    };

    session.onpaymentauthorized = (event: any) => {
      this.saveInstructions(() => {
        this.paymentGatewayService
          .walletPay(+this.store.id, this.orderHash, 'APPLEPAY', event.payment)
          .subscribe(
            () => { session.completePayment(ApplePaySession.STATUS_SUCCESS); this.succeed('APPLEPAY'); },
            err => {
              console.error('[ApplePay] charge failed', err);
              session.completePayment(ApplePaySession.STATUS_FAILURE);
              this.fail(this.messageOf(err));
            }
          );
      });
    };

    session.oncancel = () => this.ngZone.run(() => { this.activeWallet = null; this.cdr.markForCheck(); });
    session.begin();
  }

  // --------------------------------------------------------------- Google Pay

  private setUpGoogle() {
    get(GOOGLE_PAY_JS, () => this.ngZone.run(() => this.initGoogle()));
  }

  private baseCardMethod() {
    return {
      type: 'CARD',
      parameters: { allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'], allowedCardNetworks: CARD_NETWORKS }
    };
  }

  private initGoogle() {
    try {
      this.paymentsClient = new google.payments.api.PaymentsClient({
        environment: this.config.google_environment || 'TEST'
      });
      this.paymentsClient
        .isReadyToPay({ apiVersion: 2, apiVersionMinor: 0, allowedPaymentMethods: [this.baseCardMethod()] })
        .then((response: any) => this.ngZone.run(() => {
          this.showGoogle = !!response.result;
          this.cdr.detectChanges();
          if (this.showGoogle && this.googleButton) {
            const button = this.paymentsClient.createButton({
              buttonColor: 'black',
              buttonType: 'pay',
              buttonSizeMode: 'fill',
              onClick: () => this.ngZone.run(() => this.payWithGoogle())
            });
            this.googleButton.nativeElement.innerHTML = '';
            this.googleButton.nativeElement.appendChild(button);
          }
        }))
        .catch(() => { this.showGoogle = false; this.cdr.markForCheck(); });
    } catch (e) {
      this.showGoogle = false;
    }
  }

  payWithGoogle() {
    if (!this.preflight()) { return; }
    this.activeWallet = 'GOOGLEPAY';
    const request: any = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [{
        ...this.baseCardMethod(),
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: { gateway: 'worldnet', gatewayMerchantId: this.config.gateway_merchant_id }
        }
      }],
      merchantInfo: { merchantName: this.config.store_name || 'Order' },
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: this.total.toFixed(2),
        currencyCode: 'USD',
        countryCode: 'US'
      }
    };
    if (this.config.google_merchant_id) {
      request.merchantInfo.merchantId = this.config.google_merchant_id;
    }

    this.paymentsClient.loadPaymentData(request)
      .then((paymentData: any) => this.ngZone.run(() => {
        this.saveInstructions(() => {
          this.paymentGatewayService
            .walletPay(+this.store.id, this.orderHash, 'GOOGLEPAY', paymentData)
            .subscribe(() => this.succeed('GOOGLEPAY'), err => this.fail(this.messageOf(err)));
        });
      }))
      .catch((err: any) => this.ngZone.run(() => {
        console.error('[GooglePay] failed', err);
        if (err && err.statusCode === 'CANCELED') {
          this.activeWallet = null;
          this.cdr.markForCheck();
        } else {
          this.fail('Google Pay could not be completed. Please try another payment method.');
        }
      }));
  }

  // ------------------------------------------------------------------ shared

  /** The checks onSubmit() makes before a hosted-page payment, so a wallet cannot skip them. */
  private preflight(): boolean {
    if (this.disabled || this.paying) { return false; }
    const bo = this.orderWithPayment?.bot_order;
    const payment = this.orderWithPayment?.payment;
    const store = this.orderWithPayment?.store;
    if (!bo?.occasion) {
      this.snackBar.open('Select Order Type', 'OK', { verticalPosition: 'top' });
      return false;
    }
    if (store && !store.isStoreOpened()) {
      this.snackBar.open('The store is closed right now.', 'OK', { verticalPosition: 'top' });
      return false;
    }
    if (bo.occasion === 'DELIVERY' && store?.delivery_minimum
        && (payment.sub_total + payment.store_credit) < store.delivery_minimum) {
      this.snackBar.open(`Minimum order value for delivery is $${store.delivery_minimum}`, 'OK', { verticalPosition: 'top' });
      return false;
    }
    if (this.total <= 0) {
      this.snackBar.open('Nothing to pay on this order.', 'OK', { verticalPosition: 'top' });
      return false;
    }
    return true;
  }

  /** Order name and instructions are saved before the sale, as onSubmit() does for the hosted page. */
  private saveInstructions(next: () => void) {
    this.orderService
      .updateOrderInstructions(this.orderHash, this.orderInstruction, this.orderName)
      .subscribe(() => next(), () => next());
  }

  private succeed(wallet: string) {
    this.analyticsService.trackEvent('purchase', {
      order_hash: this.orderHash, cart_total: this.total, payment_method: wallet.toLowerCase(),
      store_id: this.store.id
    });
    this.ngZone.run(() => this.router.navigate(['order', this.orderHash, 'status', 'success']));
  }

  private abort(session: any, message: string) {
    try { session.abort(); } catch (e) { /* the sheet is already gone */ }
    this.fail(message);
  }

  private fail(message: string) {
    this.ngZone.run(() => {
      this.activeWallet = null;
      this.snackBar.open(message, 'OK', { verticalPosition: 'top', duration: 6000 });
      this.cdr.markForCheck();
    });
  }

  private messageOf(err: any, fallback = 'Payment could not be completed. Please try another payment method.'): string {
    return err?.error?.error || fallback;
  }
}
